import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canEverywhere, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { myLanes } from '../lib/support'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { GALLERY_BROWSE, GALLERY_MAP, HOME, PHOTO_REVIEW, PHOTO_ROOM, PHOTO_WHAT, SITE_LIST } from '../lib/hubCopy'
import { loadBrowseForEveryone, setBrowseForEveryone } from '../lib/photoReview'
import { GalleryBrowse } from '../components/GalleryBrowse'
import {
  FACETS,
  describePhotos,
  galleryUnreadCount,
  placeAllPhotos,
  loadPhotoRoom,
  loadVocabulary,
  photoUsedReward,
  searchPhotoRoom,
  type RoomPhoto,
  type RoomSort,
  type VocabValue,
} from '../lib/photoRoom'
import { AppShell } from '../components/AppShell'
import { PhotoRoomViewer } from '../components/PhotoRoomViewer'
import { RoomThumb } from '../components/RoomThumb'
import { PhotosBar } from '../components/PhotosBar'
import { HelpButton } from '../components/HelpButton'
import { TagListTray } from '../components/TagListTray'
import { SiteListTray } from '../components/SiteListTray'
import { GalleryAddDialog } from '../components/GalleryAddDialog'
import { PlaceIcon, SearchIcon, SlidersIcon } from '../components/icons'
import { GalleryMapPanel } from '../components/GalleryMapPanel'
import { loadGalleryMap, readMapPref, writeMapPref, type GalleryMapData, type MapPick } from '../lib/galleryMap'
import { MAPTILER_KEY } from '../lib/mapKey'

/** When it was posted: a few presets and a custom range (10 Oct). */
type DatePreset = 'any' | 'd7' | 'd30' | 'y1' | 'custom'
const DATE_PRESETS: DatePreset[] = ['any', 'd7', 'd30', 'y1', 'custom']

/** The preset as the server's inclusive local dates. Today in the browser's
 *  calendar; the server reads the dates in `tz`. */
function dateRange(preset: DatePreset, from: string, to: string): { from: string | null; to: string | null } {
  if (preset === 'custom') return { from: from || null, to: to || null }
  const days = preset === 'd7' ? 7 : preset === 'd30' ? 30 : preset === 'y1' ? 365 : 0
  if (!days) return { from: null, to: null }
  const d = new Date()
  d.setDate(d.getDate() - (days - 1))
  const pad = (n: number) => String(n).padStart(2, '0')
  return { from: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, to: null }
}

/**
 * `#/photos/gallery` — the gallery, which was the photo room (145, FED-48;
 * 146) until 10 Oct, when David moved it under Photos beside the contests and
 * asked for one tight toolbar: search, when it was posted, sort, Filters.
 * `photo.room`. `#/photo-room` redirects here.
 *
 * ── TWO WAYS TO LOOK, ONE LIST ──────────────────────────────────────────────
 *
 * Browsing: photo_room_photos pages the room 48 at a time, filtered by the
 * vocabulary (OR within a facet, AND across) and sorted by the server — date
 * posted or taken, downloads, winners, size (rule 1: the server sorts, and
 * "Show more" asks for the next page).
 *
 * Searching: the plain-language box goes to the photo-room Edge Function,
 * which takes the top 60 by text match and has OneConetec score each 0–100.
 * That set is the WHOLE answer to the search, so it may be re-sorted here
 * (rule 1's stated exception: the caller holds every row it sorts) — and the
 * page says it is the closest 60 of how many matched.
 *
 * ── THE ROOM DESCRIBES ITSELF, A FEW AT A TIME ──────────────────────────────
 *
 * On load the page asks OneConetec to describe up to three photos that have
 * no description yet, and reloads when it has. No cron (standing rule 2):
 * the room is described by being opened.
 *
 * ── THE MAP (318, FED-55; David, 29 Oct) ────────────────────────────────────
 *
 * Map opens a panel of pins, one per photo with a location, clustered when
 * zoomed out (GalleryMapPanel). It reads photo_room_map with the SAME filters
 * as the list, or, during a search, the search's own set. Choosing a pin or
 * a cluster narrows the list to those photos: browsing, the database filters
 * (p_paths, rule 1); searching, the set is already all here (rule 1's stated
 * exception). Any change of filters or search drops the choice, because it
 * named photos from the old answer. Nothing about the map is recorded.
 */
export function PhotoRoom({ me, tab = 'photos' }: { me: Me; tab?: 'photos' | 'contests' }) {
  const lang = useHubLang()
  const t = PHOTO_ROOM[lang]
  const holder = can(me, 'photo.room')
  /* 149. Photo room admin: where it was taken, the full-size copy, the reason.
     152: the Gallery admin — also who downloaded, the tag list, the reviews. */
  const roomAdmin = can(me, 'photo.location')
  const tenantId = me.tenants[0]?.id ?? null
  /* 152. The browser view is for anybody who may browse but is not a document
     user; the switch that opens it to everyone is People and roles' to throw. */
  const browser = !holder && canGallery(me)
  // 153: a company-wide switch, so a company-wide grant — never a regional
  // HR Administrator's admin.roles (set_photo_policy refuses them too).
  const canSwitch = canEverywhere(me, 'admin.roles')
  const tb = GALLERY_BROWSE[lang]
  const tr = PHOTO_REVIEW[lang]
  const [everyone, setEveryone] = useState<boolean | null>(null)
  useEffect(() => {
    if (!canSwitch || !tenantId) return
    loadBrowseForEveryone(tenantId).then(setEveryone).catch(() => undefined)
  }, [canSwitch, tenantId])

  const [vocab, setVocab] = useState<VocabValue[]>([])
  const [reward, setReward] = useState<number | null>(null)
  const [photos, setPhotos] = useState<RoomPhoto[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [sort, setSort] = useState<RoomSort | 'relevance'>('posted')
  const [values, setValues] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState<{
    query: string
    scores: Record<string, { score: number; why: string }>
    total: number
    scored: number
    scoredBy: 'oneconetec' | 'text'
  } | null>(null)
  const [searching, setSearching] = useState(false)
  const [describing, setDescribing] = useState(0)
  const [viewing, setViewing] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  /* 318. The map: open or not (this browser only), its data, and the pin chosen. */
  const tm = GALLERY_MAP[lang]
  const [mapOn, setMapOn] = useState(() => readMapPref())
  const [mapData, setMapData] = useState<GalleryMapData | null>(null)
  const [mapError, setMapError] = useState<string | null>(null)
  const [pick, setPick] = useState<MapPick | null>(null)
  /* 319. A photo placed in the viewer: the map reads again. */
  const [mapRev, setMapRev] = useState(0)
  const pickPaths = pick ? pick.paths : null
  /* 302 (FED-70). The Gallery admin's tag list: what One is told about each tag. */
  const [tagList, setTagList] = useState(false)
  /* 321 (FED-74): the site list, kept by the Gallery admin beside the tag list. */
  const [siteList, setSiteList] = useState(false)
  /* 304 (FED-71). Add photos straight to the Gallery — Gallery admins. */
  const [addOpen, setAddOpen] = useState(false)
  /* 304c. How many photos One has not read (admins), and the loop that reads
     them: describePhotos() three at a time until a call reads nothing. */
  const [unread, setUnread] = useState<number | null>(null)
  const [reading, setReading] = useState<{ done: number; total: number } | null>(null)
  const stopRead = useRef(false)
  useEffect(() => {
    if (!roomAdmin || tab !== 'photos' || photos === null) return
    galleryUnreadCount().then(setUnread).catch(() => setUnread(null))
  }, [roomAdmin, tab, photos])
  async function readRest() {
    const total = unread ?? 0
    if (total === 0 || reading) return
    stopRead.current = false
    setReading({ done: 0, total })
    let done = 0
    try {
      while (!stopRead.current) {
        const r = await describePhotos()
        const ok = r.filter((d) => d.ok).length
        // Nothing left, or nothing One could read this time: the same photos would come back.
        if (r.length === 0 || ok === 0) break
        done += ok
        setReading({ done, total })
      }
    } catch {
      /* the count below says where it got to */
    }
    setReading(null)
    galleryUnreadCount().then(setUnread).catch(() => {})
    if (done > 0 && !search) void browse()
  }
  /* When it was posted (David, 10 Oct). photo_room_photos and the search both
     take p_from / p_to / p_tz already (146) — a date in the viewer's own zone. */
  const [preset, setPreset] = useState<DatePreset>('any')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const tz = me.timezone ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || null)
  const range = dateRange(preset, from, to)
  const rangeKey = `${range.from ?? ''}|${range.to ?? ''}`


  useEffect(() => {
    if (!holder) return
    loadVocabulary().then(setVocab).catch(() => undefined)
    if (tenantId) photoUsedReward(tenantId).then(setReward).catch(() => undefined)
  }, [holder, tenantId])

  // Latest-wins: a slow page for the filters you left must not land under the
  // filters you chose (124's race).
  const seq = useRef(0)
  const browse = useCallback(async () => {
    if (!holder) return
    const mine = ++seq.current
    setError(null)
    try {
      const page = await loadPhotoRoom({ values, ...range, tz, sort: sort === 'relevance' ? 'posted' : sort, paths: pickPaths })
      if (mine !== seq.current) return
      setPhotos(page.photos)
      setTotal(page.total)
    } catch (e) {
      if (mine === seq.current) setError(messageOf(e) || t.couldNotLoad)
    }
    // rangeKey stands for the range: a fresh object each render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holder, values, sort, rangeKey, tz, t.couldNotLoad, pick?.key])

  const runSearch = useCallback(
    async (q: string) => {
      const mine = ++seq.current
      setSearching(true)
      setError(null)
      try {
        const r = await searchPhotoRoom(q, { values, ...range, tz })
        if (mine !== seq.current) return
        setSearch({ query: q, scores: r.scores, total: r.total, scored: r.scored, scoredBy: r.scoredBy })
        setPhotos(r.photos)
        setTotal(r.photos.length)
        setSort('relevance')
      } catch (e) {
        if (mine === seq.current) setError(messageOf(e))
      } finally {
        if (mine === seq.current) setSearching(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, rangeKey, tz],
  )

  useEffect(() => {
    if (tab !== 'photos') return
    if (search) void runSearch(search.query)
    else void browse()
    // Filters change the answer either way; the sort is applied below for a search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, values, rangeKey, search ? null : sort])

  // 318. A pin chosen while browsing asks the database again; while
  // searching, `shown` narrows the held set below.
  const firstPick = useRef(true)
  useEffect(() => {
    if (firstPick.current) {
      firstPick.current = false
      return
    }
    if (tab === 'photos' && !search) void browse()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick?.key])

  // A choice names photos from the answer it was made on; a new answer drops it.
  useEffect(() => {
    setPick(null)
  }, [values, rangeKey, search?.query])

  // The pins: the filters while browsing, the search's own set while searching.
  const searchKey = search && photos ? `${search.query}|${photos.map((p) => p.path).join(',')}` : null
  const mapSeq = useRef(0)
  useEffect(() => {
    if (!holder || tab !== 'photos' || !mapOn) return
    if (search && !photos) return
    const mine = ++mapSeq.current
    setMapError(null)
    const q = search ? { paths: (photos ?? []).map((p) => p.path) } : { values, ...range, tz }
    loadGalleryMap(q)
      .then((d) => mine === mapSeq.current && setMapData(d))
      .catch((e) => mine === mapSeq.current && setMapError(messageOf(e) || tm.failed))
    // rangeKey stands for the range; searchKey for the search's set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holder, tab, mapOn, values, rangeKey, tz, searchKey, mapRev])

  function toggleMap() {
    const on = !mapOn
    setMapOn(on)
    writeMapPref(on)
    if (!on) setPick(null)
  }

  // The room describes itself a few photos at a time.
  useEffect(() => {
    if (!holder || tab !== 'photos') return
    let live = true
    describePhotos()
      .then((done) => {
        if (!live) return
        const n = done.filter((d) => d.ok).length
        setDescribing(0)
        if (n > 0 && !search) void browse()
      })
      .catch(() => live && setDescribing(0))
    setDescribing(3)
    /* 149. And names the place of photos with coordinates. Admins only;
       nothing to show for it until a viewer is opened. 322: all of them,
       three at a time, while the page is open - not three per visit. */
    if (roomAdmin) placeAllPhotos(() => !live).catch(() => {})
    return () => {
      live = false
    }
    // Once per visit to the tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holder, tab])

  async function more() {
    if (!photos || search) return
    setLoadingMore(true)
    try {
      const next = await loadPhotoRoom({ values, ...range, tz, sort: sort === 'relevance' ? 'posted' : sort, paths: pickPaths }, photos.length)
      setPhotos((cur) => {
        const have = new Set((cur ?? []).map((p) => p.path))
        return [...(cur ?? []), ...next.photos.filter((p) => !have.has(p.path))]
      })
      setTotal(next.total)
    } catch {
      setError(t.couldNotLoad)
    } finally {
      setLoadingMore(false)
    }
  }

  // A search's set is the whole answer to it, so it may be ordered here.
  const shown = (() => {
    if (!photos) return null
    if (!search) return photos
    const chosen = pickPaths ? new Set(pickPaths) : null
    const held = chosen ? photos.filter((p) => chosen.has(p.path)) : photos
    const score = (p: RoomPhoto) => search.scores[p.path]?.score ?? 0
    const by: Record<string, (a: RoomPhoto, b: RoomPhoto) => number> = {
      relevance: (a, b) => score(b) - score(a),
      posted: (a, b) => b.postedAt.localeCompare(a.postedAt),
      taken: (a, b) => (b.takenAtLocal ?? '').localeCompare(a.takenAtLocal ?? ''),
      downloads: (a, b) => b.downloadCount - a.downloadCount,
      winners: (a, b) => b.wins.length - a.wins.length,
      resolution: (a, b) =>
        (b.originalWidth ?? b.width ?? 0) * (b.originalHeight ?? b.height ?? 0) -
        (a.originalWidth ?? a.width ?? 0) * (a.originalHeight ?? a.height ?? 0),
    }
    return [...held].sort(by[sort] ?? by.relevance)
  })()

  const label = (v: { labelEn: string; labelEs: string }) => (lang === 'es' ? v.labelEs : v.labelEn)

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Gallery"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  // 152. Not a document user: the browser's feed, if browsing is open to them.
  if (browser) {
    return shell(
      <div className="gallery">
        <PhotosBar section="gallery" canGallery />
        <GalleryBrowse />
      </div>,
    )
  }

  if (!holder) {
    return shell(
      <p className="roles__error" role="alert">
        {t.refused}
      </p>,
    )
  }

  const dateOf = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' })

  const activeValues = vocab.filter((v) => values.includes(v.id))
  const filterCount = values.length + (preset === 'any' ? 0 : 1)

  return shell(
    <div className="gallery">
      <PhotosBar section="gallery" canGallery>
        {/* 152. Admin extras: the review queue. */}
        {roomAdmin && (
          <Link className="btn btn--quiet btn--sm" to="/photos/reviews">
            {tr.queueLink}
          </Link>
        )}
        {roomAdmin && tenantId && (
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setTagList(true)}>
            {PHOTO_WHAT[lang].listButton}
          </button>
        )}
        {roomAdmin && (
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setSiteList(true)}>
            {SITE_LIST[lang].button}
          </button>
        )}
      </PhotosBar>
      {canSwitch && everyone !== null && (
        <label className="gallery__policy">
          <input
            type="checkbox"
            checked={everyone}
            onChange={(e) => {
              const on = e.target.checked
              setEveryone(on)
              setBrowseForEveryone(on).catch(() => setEveryone(!on))
            }}
          />{' '}
          {tb.policyLabel}
          <span className="gallery__policynote">{tb.policyNote}</span>
        </label>
      )}

      {/* One row (David, 10 Oct: "the search bar smaller, tighter with search,
          date filter and other filters"): the plain-language box, when it was
          posted, the sort, and everything else behind Filters. */}
      <form
        className="phototools"
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          const q = query.trim()
          if (q) void runSearch(q)
        }}
      >
        <div className="phototools__search">
          <span className="phototools__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <label className="sr-only" htmlFor="photoroom-q">
            {t.searchLabel}
          </label>
          <input
            id="photoroom-q"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            maxLength={300}
          />
          {search && (
            <button
              type="button"
              className="phototools__clear"
              aria-label={t.clearSearch}
              title={t.clearSearch}
              onClick={() => {
                setSearch(null)
                setQuery('')
                setSort('posted')
              }}
            >
              ×
            </button>
          )}
          <button type="submit" className="btn btn--primary btn--sm phototools__go" disabled={searching || !query.trim()}>
            {searching ? t.searching : t.search}
          </button>
        </div>

        <label className="phototools__field">
          <span className="sr-only">{t.dateLabel}</span>
          <select value={preset} onChange={(e) => setPreset(e.target.value as DatePreset)} aria-label={t.dateLabel}>
            {DATE_PRESETS.map((d) => (
              <option key={d} value={d}>
                {d === 'any' ? `${t.dateLabel}: ${t.datePreset.any}` : t.datePreset[d]}
              </option>
            ))}
          </select>
        </label>

        <label className="phototools__field">
          <span className="sr-only">{t.sortLabel}</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as RoomSort | 'relevance')} aria-label={t.sortLabel}>
            {search && <option value="relevance">{t.sort.relevance}</option>}
            <option value="posted">{t.sort.posted}</option>
            <option value="taken">{t.sort.taken}</option>
            <option value="downloads">{t.sort.downloads}</option>
            <option value="winners">{t.sort.winners}</option>
            <option value="resolution">{t.sort.resolution}</option>
          </select>
        </label>

        <button
          type="button"
          className={`btn btn--quiet btn--sm phototools__filters${filtersOpen ? ' is-open' : ''}`}
          aria-expanded={filtersOpen}
          aria-controls="photoroom-filters"
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <SlidersIcon size={15} />
          {t.filtersButton}
          {values.length > 0 && <span className="phototools__n">{values.length}</span>}
        </button>

        {/* 318: the map (David, 29 Oct), for document users and admins. */}
        <button
          type="button"
          className={`btn btn--quiet btn--sm phototools__mapbtn${mapOn ? ' is-open' : ''}`}
          aria-pressed={mapOn}
          aria-controls="gallery-map"
          onClick={toggleMap}
        >
          <PlaceIcon size={15} />
          {tm.toggle}
        </button>

        {/* 304: photos in without the feed (David, 22 Oct), for Gallery admins. */}
        {roomAdmin && (
          <button type="button" className="btn btn--primary btn--sm phototools__add" onClick={() => setAddOpen(true)}>
            {t.addPhotos}
          </button>
        )}
        {/* The paragraph that used to open the page (David, 10 Oct). */}
        <HelpButton title={t.helpTitle} label={t.helpLabel} className="phototools__help">
          {t.help.map((para) => (
            <p key={para}>{para}</p>
          ))}
          {reward ? <p>{t.ledeStars(reward).trim()}</p> : null}
        </HelpButton>
      </form>

      {preset === 'custom' && (
        <div className="phototools__range">
          <label>
            {t.dateFrom}
            <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            {t.dateTo}
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      )}

      <div id="photoroom-filters" className="phototools__panel" hidden={!filtersOpen}>
        {FACETS.map((facet) => (
          <div key={facet} className="phototools__facet">
            <h3 className="phototools__fh">{t.facet[facet]}</h3>
            <div className="photoroom__pills">
              {vocab
                .filter((v) => v.facet === facet && !v.retired)
                .map((v) => {
                  const on = values.includes(v.id)
                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={on ? 'chip chip--sm chip--on' : 'chip chip--sm'}
                      aria-pressed={on}
                      onClick={() => setValues((cur) => (on ? cur.filter((x) => x !== v.id) : [...cur, v.id]))}
                    >
                      {label(v)}
                    </button>
                  )
                })}
            </div>
          </div>
        ))}
      </div>

      {filterCount > 0 && (
        <div className="phototools__active">
          {activeValues.map((v) => (
            <button
              key={v.id}
              type="button"
              className="chip chip--sm chip--on"
              aria-label={`${label(v)} ×`}
              onClick={() => setValues((cur) => cur.filter((x) => x !== v.id))}
            >
              {label(v)} <span aria-hidden="true">×</span>
            </button>
          ))}
          {preset !== 'any' && (
            <button type="button" className="chip chip--sm chip--on" onClick={() => setPreset('any')}>
              {t.dateLabel}: {preset === 'custom' ? [from, to].filter(Boolean).join(' – ') || t.datePreset.custom : t.datePreset[preset]}{' '}
              <span aria-hidden="true">×</span>
            </button>
          )}
          <button
            type="button"
            className="btn btn--link btn--sm"
            onClick={() => {
              setValues([])
              setPreset('any')
            }}
          >
            {t.clearFilters}
          </button>
        </div>
      )}

      <div className={mapOn ? 'gallery__body gallery__body--map' : 'gallery__body'}>
      {mapOn && (
        <GalleryMapPanel data={mapData} error={mapError} picked={pick} onPick={setPick} t={tm} mapKey={MAPTILER_KEY} />
      )}
      <div className="gallery__results">
      {pick && (
        <p className="gallery__picked" role="status">
          {tm.picked(pick.paths.length, pick.place)}{' '}
          <button type="button" className="btn btn--link btn--sm" onClick={() => setPick(null)}>
            {tm.clear}
          </button>
        </p>
      )}
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {shown === null && !error && <p className="queue__empty">{t.loading}</p>}
      {shown !== null && shown.length === 0 && (
        <p className="queue__empty">{search ? t.noMatches : filterCount ? t.emptyFiltered : t.empty}</p>
      )}

      {shown !== null && shown.length > 0 && (
        <>
          <p className="photoroom__count">
            {search
              ? search.scoredBy === 'text'
                ? t.textOrderNote
                : t.scoredNote(search.scored, search.total)
              : t.shownOf(shown.length, total)}
            {describing > 0 && <span className="photoroom__describing"> · {t.describing(describing)}</span>}
            {/* 304c: the backlog, and the offer to clear it — admins only. */}
            {roomAdmin && !reading && describing === 0 && (unread ?? 0) > 0 && (
              <span className="photoroom__describing">
                {' · '}
                {t.unreadLine(unread ?? 0)}{' '}
                <button type="button" className="btn btn--link" onClick={() => void readRest()}>
                  {t.readRest}
                </button>
              </span>
            )}
            {reading && (
              <span className="photoroom__describing" role="status">
                {' · '}
                {t.readingRest(reading.done, reading.total)}{' '}
                <button type="button" className="btn btn--link" onClick={() => { stopRead.current = true }}>
                  {t.readStop}
                </button>
              </span>
            )}
          </p>
          <ul className="photoroom">
            {shown.map((p, i) => {
              const s = search?.scores[p.path]
              const tags = p.features.filter((f) => f.state === 'accepted' || (f.confidence ?? 0) >= 0.6).slice(0, 3)
              return (
                <li key={p.path} className="photoroom__item">
                  <button type="button" className="photoroom__open" onClick={() => setViewing(i)} aria-label={p.caption ?? t.noCaption}>
                    <RoomThumb path={p.path} alt={p.caption ?? ''} className="photoroom__img" />
                    {(s || p.wins.length > 0 || p.originalWidth) && (
                      <span className="photoroom__flags">
                        {s && <span className="photoroom__flag">{t.match(s.score)}</span>}
                        {p.wins.length > 0 && <span className="photoroom__flag photoroom__flag--win">{t.wonBadge}</span>}
                        {p.originalWidth && <span className="photoroom__flag">{t.fullSizeBadge}</span>}
                      </span>
                    )}
                  </button>
                  <div className="photoroom__body">
                    <p className={p.caption ? 'photoroom__caption' : 'photoroom__caption photoroom__caption--none'}>
                      {p.caption ?? t.noCaption}
                    </p>
                    <p className="photoroom__meta">
                      {p.bylineName ? `${p.bylineName} · ${HOME[lang].postedBy(p.uploaderName)}` : p.uploaderName} · {dateOf(p.postedAt)}
                      {p.downloadCount > 0 ? ` · ${t.downloadsShort(p.downloadCount)}` : ''}
                    </p>
                    {s?.why && <p className="photoroom__meta photoroom__why">{s.why}</p>}
                    {tags.length > 0 && (
                      <p className="photoroom__topics">
                        {tags.map((f) => (
                          <span key={f.valueId} className={f.state === 'accepted' ? 'photoroom__topic' : 'photoroom__topic photoroom__topic--suggested'}>
                            {label(f)}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
          {!search && shown.length < total && (
            <button type="button" className="btn btn--quiet btn--sm photoroom__more" disabled={loadingMore} onClick={() => void more()}>
              {t.showMore}
            </button>
          )}
        </>
      )}
      </div>
      </div>

      {addOpen && roomAdmin && tenantId && (
        <GalleryAddDialog
          open
          onClose={() => setAddOpen(false)}
          tenantId={tenantId}
          personId={me.personId}
          vocab={vocab}
          onAdded={() => {
            if (search) void runSearch(search.query)
            else void browse()
          }}
        />
      )}

      {viewing !== null && shown && shown[viewing] && (
        <PhotoRoomViewer
          photos={shown}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          vocab={vocab}
          me={{ personId: me.personId, displayName: me.displayName }}
          reward={reward}
          score={search?.scores[shown[viewing].path] ?? null}
          onChange={(np) => setPhotos((cur) => (cur ?? []).map((x) => (x.path === np.path ? np : x)))}
          onWithdrawn={(path) => {
            setViewing(null)
            setPhotos((cur) => (cur ?? []).filter((x) => x.path !== path))
            setTotal((n) => Math.max(0, n - 1))
          }}
          onVocabAdded={(v) => setVocab((cur) => [...cur, v])}
          onRefresh={async () => {
            if (search) await runSearch(search.query)
            else await browse()
          }}
          canLocation={roomAdmin}
          onPlaced={() => {
            setMapRev((n) => n + 1)
            // An admin's visit names new places (149); do it now for the new point.
            if (roomAdmin) placeAllPhotos().catch(() => {})
          }}
        />
      )}
      {tagList && tenantId && <TagListTray tenantId={tenantId} onClose={() => setTagList(false)} />}
      {siteList && <SiteListTray onClose={() => setSiteList(false)} />}
    </div>,
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
